// Import app config.
import AppConfig from '@config/app';
// Import interfaces.
import {
	IAuthentication,
} from '@app/interfaces';
// Import interface parameters.
import {
	GetAccessTokenParameters,
	InitiateParameters,
	LogoutParameters,
	RefreshAccessTokenParameters,
	SearchAndLoadLoginSessionParameters,
} from '@app/interfaces/parameters';
// Import msal browser modules.
import {
	AccountInfo,
	AuthenticationResult,
	BrowserAuthError,
	BrowserAuthOptions,
	BrowserSystemOptions,
	CacheOptions,
	ClientAuthError,
	Configuration,
	InteractionRequiredAuthError,
	PublicClientApplication,
	SilentRequest,
} from '@azure/msal-browser';

type SSOConfig = typeof AppConfig.sso_config;

export default class MsalAuthenticationService implements IAuthentication {

	private ssoConfig: SSOConfig;
	private msalInstance: PublicClientApplication;

	public constructor() {
		//Read the configuration for the SSO.
		this.ssoConfig = AppConfig.sso_config;

		// Setup authority.
		const instance: string = this.ssoConfig.instanceUri;
		const authority: string = '' + instance + this.ssoConfig.tenant + '/' + this.ssoConfig.signInPolicy;

		// Set up the authorization configuration.
		const auth: BrowserAuthOptions = {
			clientId: this.ssoConfig.applicationId,
			authority: authority,
			knownAuthorities: [instance],
			redirectUri: this.ssoConfig.redirectUri,
			postLogoutRedirectUri: this.ssoConfig.postLogoutRedirectUri,
		};

		// Setup caching configuration.
		const cacheOptions: CacheOptions = {
			cacheLocation: this.ssoConfig.cacheLocation,
			storeAuthStateInCookie: false,
			secureCookies: false,
		};

		// Setup system configuration.
		const loggerCallback = (logLevel, message, piiLoggingEnabled): void => {
			//console.log(message);
		};

		const systemOptions: BrowserSystemOptions = {
			loggerOptions: {
				loggerCallback: this.ssoConfig.enableAuthenticationLogging ? loggerCallback : undefined,
			}
		};

		// Initialize user agent application.
		let msalConfig: Configuration = {
			auth: auth,
			cache: cacheOptions,
			system: systemOptions,
		}

		this.msalInstance = new PublicClientApplication(msalConfig);
	}

	/**
	 * Fetches the access token with the possible redirect.
	 * @param params
	 */
	public getAccessTokenWithRedirect(params: GetAccessTokenParameters): void {
		let request: SilentRequest = {
			scopes: this.ssoConfig.scopes,
		};

		this.msalInstance.acquireTokenSilent(request).then((response: AuthenticationResult) => {
			params.getTokenSuccessCallback(response.accessToken);
		}).catch((error: any) => {
			if (error instanceof InteractionRequiredAuthError) {
				// Do acquire token redirect.
				this.msalInstance.acquireTokenRedirect(request);
			}
			else if (error instanceof ClientAuthError || error instanceof BrowserAuthError) {
				// Do login redirect.
				this.msalInstance.loginRedirect(request);
			}
			else {
				params.getTokenFailCallback()
			}
		});
	}

	/**
	 * Force refreshes the lifetime of the token.
	 * @param params
	 */
	public refreshTokenWithRedirect(params: RefreshAccessTokenParameters): void {
		let request: SilentRequest = {
			scopes: this.ssoConfig.scopes,
			forceRefresh: true,
		};

		this.msalInstance.acquireTokenSilent(request).then((response: AuthenticationResult) => {
			params.refreshTokenSuccessCallback(response.accessToken);
		}).catch((error: any) => {
			if (error instanceof InteractionRequiredAuthError) {
				// Do acquire token redirect.
				this.msalInstance.acquireTokenRedirect(request);
			}
			else if (error instanceof ClientAuthError || error instanceof BrowserAuthError) {
				// Do login redirect.
				this.msalInstance.loginRedirect(request);
			}
			else {
				params.refreshTokenFailCallback();
			}
		});
	}

	/**
	 * Searches and loads any existing login session.
	 * @param params
	 */
	public searchAndLoadExistingLogin(params: SearchAndLoadLoginSessionParameters): void {
		this.msalInstance.ssoSilent({
			scopes: this.ssoConfig.scopes,
		}).then((response: AuthenticationResult) => {
			params.searchAndLoadLoginSessionSuccessCallback(response.accessToken);
		}).catch((error: any) => {
			params.searchAndLoadLoginSessionFailCallback();
		});
	}

	/**
	 * Invokes a login redirect call. 
	 **/
	public login(): void {
		this.msalInstance.loginRedirect({
			scopes: this.ssoConfig.scopes,
		});
	}

	/**
	 * Initiates the login redirect process, if there is any.
	 * @param params
	 */
	public initiate(params: InitiateParameters): void {
		// Make sure the hidden Iframe is not open before processing a redirect promise.
		if (window.self === window.top) {
			this.msalInstance.handleRedirectPromise().then((loginResponse: AuthenticationResult | null) => {
				if (loginResponse === null) {
					// Invoke the no-login-redirect-callback method, if provided.
					if (params.noLoginRedirectCallback !== undefined) {
						params.noLoginRedirectCallback();
					}
				}
				else {
					// Set the active account.
					this.msalInstance.setActiveAccount(loginResponse.account);
					// Invoke call back for finished login redirect.
					if (params.finishLoginRedirectCallback !== undefined) {
						params.finishLoginRedirectCallback(loginResponse.accessToken);
					}
				}
			}).catch((error: any) => {
				if (params.loginRedirectFailCallback !== undefined) {
					params.loginRedirectFailCallback();
				}
			});
		}
	}

	/**
	 * Logs the current authenticated user out of the application with parameters.
	 * @param params
	 */
	public logoutWithParameters(params: LogoutParameters): void {
		let postLogoutRedirectUri: string = this.ssoConfig.postLogoutRedirectUri.format(params.appV2LoggedOut.toString(), encodeURIComponent(params.returnToUrl));
		this.logout(postLogoutRedirectUri);
	}

	/**
	 * Logs the current authenticated user out of the application.
	 */
	public logout(redirectUrl: string): void {
		this.msalInstance.logoutRedirect({
			postLogoutRedirectUri: redirectUrl
		});
	}

	/**
	 *  Get the current authenticated user.
	 */
	public getUser(): AccountInfo | null {
		return this.msalInstance.getActiveAccount();
	}

	/**
	 * Checks to see if the user is logged in. 
	 */
	public isLoggedIn(): boolean {
		return this.getUser() != null;
	}
}
